// Swup core + plugins
import Swup from "swup";
import SwupPreloadPlugin from "@swup/preload-plugin";
import SwupJsPlugin from "@swup/js-plugin";
import SwupGaPlugin from "@swup/ga-plugin";

// Components
import ImageQuoteSlider from "./components/ImageQuoteSlider";
import QuoteSlider from "./components/QuoteSlider";
import ValuesSlider from "./components/ValuesSlider";
import AwardsSlider from "./components/AwardsSlider";
import NavigationController from "./components/NavigationController";
import NavigationTabs from "./components/NavigationTabs";
import NavigationMenu from "./components/NavigationMenu";
import Selects from "./components/Selects";
import Popup from "./components/Popup";
import VideoController from "./components/VideoController";
import KeyNumbersSlider from "./components/KeyNumbersSlider";
import ResourcesSlider from "./components/ResourcesSlider";
import DropdownController from "./components/DropdownController";
import OfficeBrowserSlider from "./components/OfficeBrowserSlider";
import CF7Controller from "./components/CF7Controller";
import ScrollSpy from "./components/ScrollSpy";
import CountAnimation from "./components/CountAnimation";
import FadeAnimation from "./components/FadeAnimation";
import CursorController from "./components/CursorController";
import CustomVideoPlayer from "./components/CustomVideoPlayer";
import SisterSlider from "./components/SisterSlider";
import NewQuoteSlider from "./components/NewQuoteSlider";
import NumbersSlider from "./components/NumbersSlider";
import InsightsSlider from "./components/InsightsSlider";


// Plugins
import LazyLoad from "vanilla-lazyload";
import ScrollNext from "@bornfight/b-scroll-next";
import ScrollTo from "@bornfight/b-scroll-to";
import ScrollLock from "@bornfight/b-scroll-lock";
import Rellax from "rellax";
import gsap from "gsap";
import ResourcesFilter from "./components/ResourcesFilter";
import ResourcesNavigationFilter from "./components/ResourcesNavigationFilter";
import TeamModuleFilter from "./components/TeamModuleFilter";
import ResourceModuleFilter from "./components/ResourceModuleFilter";
import ResourceJobModuleFilter from "./components/ResourceJobModuleFilter";
import CustomizedContactFormFilter from "./components/CustomizedContactFormFilter";
import LanguagePicker from "./components/LanguagePicker";
import ChromeriverList from "./components/ChromeriverList";
import CountryStateFormFieldController from "./components/CountryStateFormFieldController";
import CF7FormValidation from "./components/CF7FormValidation";

export default class SwupController {
    constructor() {
        this.body = document.querySelector("body");
        this.document = document.documentElement || document.body;
        this.scrollLock = new ScrollLock();

        this.init();
    }

    /**
     * SWUP init - page transition/preload/etc.
     */
    init() {
        const self = this;

        // loader layers
        this.loader = document.querySelector(".js-loader");
        const loaderFirstLayer = document.querySelector(".js-loader-first-layer");
        const loaderSecondLayer = document.querySelector(".js-loader-second-layer");

        // page transition options
        const options = [
            {
                from: "(.*)",
                to: "(.*)",
                out: (next) => {
                    const startTimeline = gsap
                        .timeline({
                            onComplete: () => {
                                next();
                            },
                        })
                        .fromTo(
                            loaderFirstLayer,
                            {
                                y: "100%",
                            },
                            {
                                y: "0%",
                                duration: 0.7,
                                ease: "power2.inOut",
                            },
                        )
                        .fromTo(
                            loaderSecondLayer,
                            {
                                y: "100%",
                            },
                            {
                                y: "0%",
                                duration: 0.5,
                                ease: "power2.inOut",
                            },
                            "-=0.5",
                        );
                },
                in: (next) => {
                    const endTimeline = gsap
                        .timeline({
                            onComplete: () => {
                                next();
                            },
                        })
                        .fromTo(
                            loaderSecondLayer,
                            {
                                y: "0%",
                            },
                            {
                                y: "-100%",
                                duration: 0.7,
                                ease: "power2.inOut",
                            },
                        )
                        .fromTo(
                            loaderFirstLayer,
                            {
                                y: "0%",
                            },
                            {
                                y: "-100%",
                                duration: 0.8,
                                ease: "power2.inOut",
                            },
                            "-=0.6",
                        );
                },
            },
        ];

        const swup = new Swup({
            cache: true,
            animateHistoryBrowsing: true,
            animationSelector: '[class="transition-slide"]',
            plugins: [
                // new SwupPreloadPlugin(),
                new SwupJsPlugin(options),
                new SwupGaPlugin(),
            ],
        });

        // init components on page load
        const selects = new Selects();
        selects.init();
        self.componentsInit();

        // init components on page change and set scroll to top
        swup.on("willReplaceContent", (ev) => {
            this.document.scrollTop = 0;
            this.scrollLock.unlockScroll();
        });

        swup.on("contentReplaced", (ev) => {
            self.componentsInit();
            if (typeof window.ga !== "undefined") {
                window.ga("set", "title", document.title);
                window.ga("set", "virtualPageTitle", document.title);
                window.ga(
                    "set",
                    "page",
                    window.location.pathname + window.location.search,
                );
                window.ga(
                    "set",
                    "virtualPageURL",
                    window.location.pathname + window.location.search,
                );
                window.ga("send", "pageview");
            }
            let forms = document.querySelectorAll(".wpcf7-form");
            if (forms.length > 0) {
                forms.forEach((element) => {
                    wpcf7.init(element);
                    if (wpcf7.cached) {
                        wpcf7.cached = 0;
                    }
                });
            }

            const selects = new Selects();
            selects.init();
        });

        // initial load
        setTimeout(() => {
            const endTimeline = gsap
                .timeline({
                    onComplete: () => {
                        this.body.classList.add("is-loaded");
                    },
                })
                .fromTo(
                    loaderSecondLayer,
                    {
                        y: "0%",
                    },
                    {
                        y: "-100%",
                        duration: 0.7,
                        ease: "power2.inOut",
                    },
                )
                .fromTo(
                    loaderFirstLayer,
                    {
                        y: "0%",
                    },
                    {
                        y: "-100%",
                        duration: 0.8,
                        ease: "power2.inOut",
                    },
                    "-=0.6",
                );
        }, 300);
    }

    /**
     * all components and plugins init
     */
    componentsInit() {
        console.log("components init");

        const navigationController = new NavigationController();
        navigationController.init();

        const navigationTabs = new NavigationTabs();
        navigationTabs.init();

        const navigationMenu = new NavigationMenu();
        navigationMenu.init();

        const imageQuoteSlider = new ImageQuoteSlider();
        imageQuoteSlider.init();

        const quoteSlider = new QuoteSlider();
        quoteSlider.init();

        const valuesSlider = new ValuesSlider();
        valuesSlider.init();

        const awardsSlider = new AwardsSlider();
        awardsSlider.init();

        const sisterSlider = new SisterSlider();
        sisterSlider.init();

        const newquoteSlider = new NewQuoteSlider();
        newquoteSlider.init();

        const numbersSlider = new NumbersSlider();
        numbersSlider.init();

        const insightsSlider = new InsightsSlider();
        insightsSlider.init();

        const resourcesSlider = new ResourcesSlider();
        resourcesSlider.init();

        const officeBrowserSlider = new OfficeBrowserSlider();
        officeBrowserSlider.init();

        const keyNumbersSlider = new KeyNumbersSlider();
        keyNumbersSlider.init();

        const countAnimation = new CountAnimation();
        countAnimation.init();

        const fadeAnimation = new FadeAnimation();
        fadeAnimation.init();

        const scrollSpy = new ScrollSpy();

        const popup = new Popup();

        const dropdownController = new DropdownController();
        dropdownController.init();

        const cf7Controller = new CF7Controller();
        cf7Controller.init();

        const videoController = new VideoController();
        videoController.init();

        const resourcesFilter = new ResourcesFilter();
        resourcesFilter.init();

        const resourcesNavigationFilter = new ResourcesNavigationFilter();
        resourcesNavigationFilter.init();
        
        let teamModules = document.querySelectorAll('.team-list-module');
        teamModules.forEach(el => {
            const teamModuleFilter = new TeamModuleFilter();
            teamModuleFilter.init(el);
        });

        const resourceModuleFilter = new ResourceModuleFilter();
        resourceModuleFilter.init();

        const resourceJobModuleFilter = new ResourceJobModuleFilter();
        resourceJobModuleFilter.init();

        const customizedContactFormFilter = new CustomizedContactFormFilter();
        customizedContactFormFilter.init();

        const languagePicker = new LanguagePicker();
        languagePicker.init();

        const chromeriverList = new ChromeriverList();
        chromeriverList.init();

        const cursorController = new CursorController();
        cursorController.init();

        const customVideoPlayer = new CustomVideoPlayer();
        customVideoPlayer.init();

        const cf7FormValidation = new CF7FormValidation();
        cf7FormValidation.init();

        // class used for ebook form -> country and state fields
        const countryStateFormFieldController = new CountryStateFormFieldController();
        countryStateFormFieldController.init();

        /**
         * Lazyload init
         */
        const lazyLoad = new LazyLoad({
            elements_selector: ".js-lazy",
        });

        /**
         * next section smooth scroll
         */
        new ScrollNext({
            duration: 1,
            ease: "power3.inOut",
        });

        /**
         * scroll to plugin (data-target="#section-id")
         */
        new ScrollTo({
            duration: 0.8,
        });

        /**
         * parallax init
         */
        const rellax = new Rellax(".rellax", {
            speed: -2,
            center: true,
            wrapper: null,
            round: true,
            vertical: true,
            horizontal: false,
        });
    }
}
